/**
 * CSAF advisory sources — the operator's vendor-feed catalogue in the
 * dedicated "CSAF-Quellen" area of the system console.
 *
 * The CSAF sync used to pull exactly one hard-wired feed (Siemens ProductCERT,
 * from the environment). Every other vendor that publishes machine-readable
 * advisories needed a redeploy. This panel moves the list into the operator's
 * hands: add a feed URL, and the next automatic or on-demand sync — matches its
 * advisories against the portfolio and writes the affected CVEs into
 * Dependency-Track as internal vulnerabilities.
 *
 * Two things share the panel because they are the same job from the operator's
 * side, "get this vendor's advisories in":
 *
 *   - a SOURCE is a URL that is polled (ROLIE/RSS/single document) — the normal
 *     case, and what the automatic monitor walks,
 *   - an UPLOAD is one CSAF document filed under a source by hand, for
 *     advisories that arrive by e-mail or sit behind a login. It is ingested
 *     immediately, so it reports the same result shape a sync does.
 *
 * Every active source imports all CVEs, independently of portfolio relevance.
 *
 * Permissions split the same way the endpoints do: reading the catalogue comes
 * with the console (`system.admin`), every write asks for `scan.run` — a source
 * decides what the server downloads and writes into DT. Without it the controls
 * stay visible but disabled, with the missing permission in the tooltip.
 */

import { msg, plural, t } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { Banner } from '../../components/Banner';
import { Chip, type ChipTone } from '../../components/Chip';
import EmptyState from '../../components/EmptyState';
import { KpiRow, KpiTile } from '../../components/KpiTile';
import ManufacturerLogo from '../../components/ManufacturerLogo';
import Spinner from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import {
  deleteCsafSource,
  errorMessage,
  fetchCsafSources,
  syncCsafSources,
  updateCsafSource,
  uploadCsafDocument,
} from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { DASH, fmtDate, fmtNumber } from '../../lib/format';
import { intlLocale } from '../../lib/locale';
import { missingPermissionTitle } from '../../lib/permissions';
import { CSAF_SOURCE_KIND_LABELS } from '../../lib/types';
import type {
  CsafSource,
  CsafSourceKind,
  CsafSourcePreset,
  CsafSourceStatus,
  CsafSourcesResponse,
  CsafSyncResult,
} from '../../lib/types';
import CsafSourceWizard from './CsafSourceWizard';
import CsafSourceVulnerabilities from './CsafSourceVulnerabilities';
import './csaf-sources.css';

/** Fallback vocabulary — replaced by the server's `kinds` once loaded. */
const FALLBACK_KINDS: readonly CsafSourceKind[] = ['rolie', 'feed', 'document', 'manual', 'euvd'];

/** Known kinds get the translated SPA label; unknown ones keep the server's. */
function kindLabel(kind: string, serverLabel: string): string {
  const labels: Readonly<Record<string, string | undefined>> = CSAF_SOURCE_KIND_LABELS;
  return labels[kind] ?? serverLabel;
}

/** Kind options for the wizard: the server's list, or the fallback before it loaded. */
function kindOptions(
  serverKinds: CsafSourcesResponse['kinds'] | null,
): { kind: CsafSourceKind; label: string }[] {
  const entries = serverKinds ?? FALLBACK_KINDS.map((kind) => ({ kind, label: kind }));
  return entries.map((entry) => ({ kind: entry.kind, label: kindLabel(entry.kind, entry.label) }));
}

/** Date of the bundled source catalogue (preset list). */
const CATALOG_DATE = new Date(2026, 8, 10);

function catalogDateLabel(): string {
  return CATALOG_DATE.toLocaleDateString(intlLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const STATUS_TONE: Readonly<Record<CsafSourceStatus, ChipTone>> = {
  ok: 'good',
  partial: 'warn',
  error: 'alert',
};

const STATUS_LABEL = {
  ok: msg`ok`,
  partial: msg`teilweise`,
  error: msg`Fehler`,
} as const satisfies Readonly<Record<CsafSourceStatus, unknown>>;

/** One-liner over a sync/upload result — the same wording either way.
 *  Translated at call time; each part is a self-contained list item. */
function summarise(result: CsafSyncResult): string {
  const checked = fmtNumber(result.cves_total);
  const matched = fmtNumber(result.cves_matched?.length);
  const created = result.created.length;
  const updated = result.updated.length;
  const parts = [
    t`${checked} CVEs geprüft`,
    t`${matched} betreffen das Portfolio`,
    t`${created} CVEs neu`,
    t`${updated} aktualisiert`,
  ];
  if (result.unchanged > 0) {
    const unchanged = fmtNumber(result.unchanged);
    parts.push(t`${unchanged} unverändert`);
  }
  if (result.skipped_nvd_enriched.length > 0) {
    const covered = result.skipped_nvd_enriched.length;
    parts.push(t`${covered} bereits über NVD abgedeckt`);
  }
  if (result.skipped_no_document.length > 0) {
    const withoutDocument = result.skipped_no_document.length;
    parts.push(t`${withoutDocument} Einträge ohne CSAF-Dokument`);
  }
  if (result.reanalyzed_projects > 0) {
    const projects = fmtNumber(result.reanalyzed_projects);
    parts.push(t`${projects} Dependency-Track-Projekte neu analysiert`);
  }
  return `${parts.join(', ')}.`;
}

interface LastRun {
  title: string;
  result: CsafSyncResult;
}

/** "<title>: <summary>" line of the last sync/upload. */
function lastRunLine({ title, result }: LastRun): string {
  const summary = summarise(result);
  return t`${title}: ${summary}`;
}

export function CsafSourcesCard(): ReactNode {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const { has } = useAuth();
  const toast = useToast();
  const canWrite = has('scan.run');
  const noScanRun = missingPermissionTitle('scan.run');

  const [monitoring, setMonitoring] = useState<CsafSourcesResponse['monitoring'] | null>(null);
  const [search, setSearch] = useState('');
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [sources, setSources] = useState<CsafSource[]>([]);
  const [serverKinds, setServerKinds] = useState<CsafSourcesResponse['kinds'] | null>(null);
  const [presets, setPresets] = useState<CsafSourcePreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Id of the source whose row mutation is in flight. */
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [lastRun, setLastRun] = useState<LastRun | null>(null);
  /** `undefined` closes the wizard; `null` opens an empty custom source. */
  const [wizardPreset, setWizardPreset] = useState<CsafSourcePreset | null | undefined>(undefined);

  /** One hidden file input, retargeted at whichever row asked for an upload. */
  const fileInput = useRef<HTMLInputElement | null>(null);
  const uploadTarget = useRef<string | null>(null);

  const load = useCallback(async (silent = false): Promise<void> => {
    if (!silent) setLoading(true);
    try {
      const response = await fetchCsafSources();
      setSources(response.sources);
      setMonitoring(response.monitoring);
      if (response.kinds.length > 0) setServerKinds(response.kinds);
      setPresets(response.presets ?? []);
      setLoadError(null);
    } catch (err) {
      setLoadError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => { void load(true); }, 30000);
    return () => window.clearInterval(timer);
  }, [load]);

  const kinds = kindOptions(serverKinds);

  /** Report a sync/upload outcome: 'partial' keeps its errors on screen. */
  const report = useCallback(
    (title: string, result: CsafSyncResult): void => {
      setLastRun({ title, result });
      if (result.errors.length > 0) {
        const errorCount = result.errors.length;
        toast.error(
          t`${title}: ${plural(errorCount, { one: '# Fehler', other: '# Fehler' })}, Details in der Karte.`,
        );
      } else {
        const created = result.created.length;
        const updated = result.updated.length;
        toast.success(t`${title}: ${created} CVEs neu, ${updated} aktualisiert.`);
      }
    },
    // `locale`: the toast wording follows the UI language.
    [toast, locale],
  );

  function onWizardCreated(source: CsafSource): void {
    setSources((rows) => [...rows, source].sort((a, b) => a.name.localeCompare(b.name, intlLocale())));
    setWizardPreset(undefined);
    const { name } = source;
    toast.success(t`Quelle „${name}“ hinzugefügt und für den nächsten CSAF-Sync aktiviert.`);
  }

  async function onToggle(source: CsafSource): Promise<void> {
    setBusy(source.id);
    try {
      const response = await updateCsafSource(source.id, { enabled: !source.enabled });
      setSources((rows) => rows.map((row) => (row.id === source.id ? response.source : row)));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function onDelete(source: CsafSource): Promise<void> {
    setBusy(source.id);
    try {
      await deleteCsafSource(source.id);
      setSources((rows) => rows.filter((row) => row.id !== source.id));
      setConfirmDelete(null);
      const { name } = source;
      toast.success(t`Quelle „${name}“ entfernt.`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function onSync(source: CsafSource): Promise<void> {
    setBusy(source.id);
    try {
      const response = await syncCsafSources([source.id]);
      const { name } = source;
      report(t`Quelle „${name}“ synchronisiert`, response.result);
      await load();          // pick up the stamped last-sync state
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  function pickFile(sourceId: string): void {
    uploadTarget.current = sourceId;
    fileInput.current?.click();
  }

  async function onFileChosen(file: File): Promise<void> {
    const sourceId = uploadTarget.current;
    if (sourceId === null) return;
    const source = sources.find((row) => row.id === sourceId);
    setBusy(sourceId);
    try {
      const response = await uploadCsafDocument(sourceId, file);
      const fileName = file.name;
      const sourceName = source?.name ?? sourceId;
      report(t`Datei „${fileName}“ in „${sourceName}“ übernommen`, response.result);
      await load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
      uploadTarget.current = null;
    }
  }

  const intervalMinutes = monitoring?.interval_minutes;
  const nextRun = fmtDate(monitoring?.next_run_at);
  const presetCount = presets.length;
  const catalogDate = catalogDateLabel();

  return (
    <section className="card">
      <div className="card-head">
        <h2><Trans>CSAF-Quellen</Trans></h2>
        <Chip tone={monitoring?.enabled ? 'good' : 'warn'}>
          {monitoring?.enabled ? t`Automatische Überwachung aktiv` : t`Überwachung nicht aktiv`}
        </Chip>
      </div>
      <div className="card-body">
      <KpiRow label={t`CSAF-Überwachung`}>
        <KpiTile label={t`Überwachte Quellen`} value={sources.filter(s => s.enabled && s.kind !== 'manual').length} />
        <KpiTile label={t`Offizielle Quellenvorlagen`} value={presets.length} />
        <KpiTile label={t`Prüfintervall`} value={monitoring?.enabled ? t`${intervalMinutes} Min.` : '—'} />
      </KpiRow>
      <p className="csaf-monitor-note">
        {monitoring?.enabled
          ? t`Nächste automatische Prüfung: ${nextRun}.`
          : t`Die automatische Überwachung ist deaktiviert. Setzen Sie CSAF_SYNC_ENABLED und starten Sie den Dienst neu.`}
        {' '}<Trans>Manuelle Upload-Quellen werden nur per Datei-Upload aktualisiert.</Trans>
      </p>

      <div className="csaf-source-toolbar">
        <button
          type="button"
          className="primary"
          disabled={!canWrite || busy !== null}
          title={canWrite ? t`Quelle mit Verbindungstest einrichten` : noScanRun}
          aria-haspopup="dialog"
          onClick={() => setWizardPreset(null)}
        >
          <Trans>Neue Quelle einrichten</Trans>
        </button>
        <button type="button" className="ghost small" disabled={loading} onClick={() => void load()}>
          <Trans>Liste neu laden</Trans>
        </button>
      </div>

      {loadError !== null ? (
        <Banner kind="error">
          {loadError}
        </Banner>
      ) : null}

      {loading ? (
        <Spinner label={t`Lade Quellen…`} />
      ) : sources.length === 0 ? (
        <EmptyState title={t`Noch keine Quelle eingerichtet`}>
          <Trans>Richten Sie über „Neue Quelle einrichten“ einen Hersteller-Feed oder eine Upload-Quelle ein.</Trans>
        </EmptyState>
      ) : (
        <div className="csaf-source-grid" aria-label={t`Eingerichtete CSAF-Quellen`}>
          {sources.map(source => {
            const rowBusy = busy === source.id;
            const lastSync = fmtDate(source.last_sync_at);
            const lastCreated = fmtNumber(source.last_created ?? 0);
            const lastUpdated = fmtNumber(source.last_updated ?? 0);
            return <article key={source.id} className={`csaf-source-card${source.enabled ? '' : ' csaf-source-paused'}`}>
              <div className="csaf-source-card-head">
                <ManufacturerLogo manufacturer={source.name} className="csaf-source-logo" />
                <div><h4>{source.name}</h4><span className="muted">{kindLabel(source.kind, source.kind_label)}</span></div>
                <Chip tone={!source.enabled ? 'idle' : source.last_status ? STATUS_TONE[source.last_status] : 'info'}>
                  {!source.enabled ? t`Pausiert` : source.last_status ? i18n._(STATUS_LABEL[source.last_status]) : t`Wartet auf Prüfung`}
                </Chip>
              </div>
              <div className="csaf-source-facts">
                <div><span><Trans>CVEs geprüft</Trans></span><strong>{source.last_status === null ? DASH : fmtNumber(source.last_cves)}</strong></div>
                <div><span><Trans>Davon portfolio-relevant</Trans></span><strong>{source.last_status === null ? DASH : fmtNumber(source.last_matched_cves)}</strong></div>
              </div>
              {source.last_status !== null ? <p className="csaf-source-run"><span><Trans>Zuletzt geprüft: {lastSync}</Trans></span><span><Trans><strong>{lastCreated} neue</strong> · {lastUpdated} aktualisierte CVEs im letzten Lauf</Trans></span></p> : null}
              {source.last_status !== null && (source.last_cves == null || source.last_matched_cves == null) ? <p className="csaf-monitor-note"><Trans>CVE-Anzahlen sind nach der nächsten Prüfung verfügbar.</Trans></p> : null}
              {source.last_message && source.last_status !== 'ok' ? <p className="csaf-source-warning">{source.last_message}</p> : null}
              <CsafSourceVulnerabilities source={source} />
              <div className="csaf-source-card-footer">
                <label className="check" title={!canWrite ? noScanRun : undefined}>
                  <input type="checkbox" checked={source.enabled} disabled={!canWrite || rowBusy} onChange={() => void onToggle(source)} />
                  {source.enabled ? t`Aktiv` : t`Pausiert`}
                </label>
                <details className="csaf-source-settings">
                  <summary><Trans>Einstellungen</Trans></summary>
                  <div className="csaf-source-settings-body">
                    {source.url ? <a className="ops-src-url" href={source.url} target="_blank" rel="noreferrer noopener">{source.url}</a> : <span className="muted"><Trans>Nur Uploads</Trans></span>}
                    <p className="muted"><Trans>Alle CVEs werden importiert, unabhängig von der Portfolio-Relevanz.</Trans></p>
                    <p className="muted"><Trans>Ein Eintrag pro CVE.</Trans></p>
                    <div className="ops-src-actions">
                      <button type="button" className="ghost small" disabled={!canWrite || rowBusy} onClick={() => void onSync(source)}>{rowBusy ? t`Läuft…` : t`Jetzt prüfen`}</button>
                      <button type="button" className="ghost small" disabled={!canWrite || rowBusy} onClick={() => pickFile(source.id)}><Trans>Datei hochladen</Trans></button>
                      {confirmDelete === source.id ? <>
                        <button type="button" className="danger small" disabled={rowBusy} onClick={() => void onDelete(source)}><Trans>Entfernen bestätigen</Trans></button>
                        <button type="button" className="ghost small" onClick={() => setConfirmDelete(null)}><Trans>Abbrechen</Trans></button>
                      </> : <button type="button" className="ghost small" disabled={!canWrite || rowBusy} onClick={() => setConfirmDelete(source.id)}><Trans>Entfernen</Trans></button>}
                    </div>
                  </div>
                </details>
              </div>
            </article>;
          })}
        </div>
      )}

      {/* One input for every row; `uploadTarget` says which source it belongs to. */}
      <input
        ref={fileInput}
        type="file"
        accept="application/json,.json"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';        // allow re-picking the same file
          if (file) void onFileChosen(file);
        }}
      />

      {lastRun !== null ? (
        <>
          <Banner
            kind={lastRun.result.errors.length > 0 ? 'warn' : 'ok'}
            items={lastRun.result.errors.length > 0 ? lastRun.result.errors : undefined}
          >
            <span className="ops-summary">
              {lastRunLine(lastRun)}
            </span>
          </Banner>
          <details className="ops-json">
            <summary><Trans>Rohdaten (JSON)</Trans></summary>
            <pre>{JSON.stringify(lastRun.result, null, 2)}</pre>
          </details>
        </>
      ) : null}

      {presets.length > 0 ? (
        <section className="ops-src-presets" aria-labelledby="csaf-presets-title">
          <div className="ops-src-presets-head">
            <h4 id="csaf-presets-title"><Trans>Quellenkatalog</Trans></h4>
            <Chip tone="neutral">
              {t`${plural(presetCount, { one: '# Quelle', other: '# Quellen' })} · Stand ${catalogDate}`}
            </Chip>
          </div>
          <div className="csaf-catalog-filter">
            <input type="search" aria-label={t`Hersteller oder Quelle suchen`} placeholder={t`Hersteller suchen, z. B. ABB, WAGO, Siemens …`} value={search} onChange={event => setSearch(event.target.value)} />
            <label className="check"><input type="checkbox" checked={onlyAvailable} onChange={event => setOnlyAvailable(event.target.checked)} /><Trans>Nur nicht eingerichtete Quellen</Trans></label>
          </div>
          {!presets.some(preset => preset.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()) && (!onlyAvailable || !sources.some(source => source.url === preset.url))) ? <div role="status"><EmptyState compact title={t`Keine passenden Quellen gefunden`} /></div> : null}
          <div className="ops-src-preset-grid">
            {presets.filter(preset => preset.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()) && (!onlyAvailable || !sources.some(source => source.url === preset.url))).map((preset) => {
              const configured = sources.some(
                (source) => source.url === preset.url || source.id === preset.id,
              );
              return (
                <article className="ops-src-preset" key={preset.id}>
                  <ManufacturerLogo manufacturer={preset.name} />
                  <div className="ops-src-preset-title">
                    <strong>{preset.name}</strong>
                    <Chip tone={preset.kind === 'euvd' ? 'info' : 'neutral'}>
                      {preset.kind === 'euvd' ? 'EUVD' : 'ROLIE'}
                    </Chip>
                  </div>
                  <p>{preset.description}</p>
                  <div className="ops-src-preset-actions">
                    <a href={preset.official_url} target="_blank" rel="noreferrer noopener">
                      <Trans>Offizielle Quelle</Trans>
                    </a>
                    <button
                      type="button"
                      className="ghost small"
                      disabled={!canWrite || configured || busy !== null}
                      title={!canWrite ? noScanRun : undefined}
                      aria-haspopup="dialog"
                      onClick={() => setWizardPreset(preset)}
                    >
                      {configured ? t`Eingerichtet` : t`Einrichten`}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ) : null}

      {wizardPreset !== undefined ? (
        <CsafSourceWizard
          preset={wizardPreset}
          kinds={kinds}
          onClose={() => setWizardPreset(undefined)}
          onCreated={onWizardCreated}
        />
      ) : null}
      </div>
    </section>
  );
}

export default CsafSourcesCard;
